import { zodResolver } from '@hookform/resolvers/zod';
import {
  PASSWORD_REQUIREMENTS_HINT,
  PRIVACY_URL,
  TERMS_URL,
  REFERRAL_DISCOUNT_CYCLES,
  REFERRAL_DISCOUNT_PERCENT,
  signupFormSchema,
  type SignupFormInput,
  type SignupFormValues,
} from '@groundly/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { rememberedReferral, rememberReferral } from '../affiliate/referral.js';
import { signUp } from '../auth/auth-client.js';
import termsStyles from '../auth/terms-agreement.module.css';
import { useCarriedValues } from '../auth/navigation-state.js';
import { savePendingSignup } from '../auth/pending-signup.js';
import { PreAccountGate } from '../auth/PreAccountGate.js';
import { waitBody, type SignupOutcome } from '../auth/signup-errors.js';
import { Button, InlineAction } from '../components/Button.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { InfoAlert } from '../components/InfoAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { TextField } from '../components/TextField.js';
import { PreAccountCard, PreAccountFootnote, PreAccountFrame } from '../frame/PreAccountFrame.js';

/**
 * Sign up (account sign-up `ux.md`, flow A and States: Sign up). It asks for a
 * name, an email address and a password, and nothing else.
 *
 * Whatever the address, a sign-up the server accepts goes on to Check your
 * email: the server's answer is the same whether or not the address already
 * had a login, so this page never shows anything that depends on it
 * (`patterns/auth-patterns.md`, 1).
 */
export const Route = createFileRoute('/signup/')({
  validateSearch: (search: Record<string, unknown>): { ref?: string } =>
    typeof search['ref'] === 'string' ? { ref: search['ref'] } : {},
  component: SignupPage,
});

function SignupPage() {
  return (
    <PreAccountGate>
      <SignupForm />
    </PreAccountGate>
  );
}

type Refusal = Extract<
  SignupOutcome,
  { kind: 'too-many-attempts' | 'no-response' | 'server-error' }
>;

function SignupForm() {
  const navigate = useNavigate();
  const { ref } = Route.useSearch();
  // Kept for the first log-in, which claims it (`affiliate/referral.ts`).
  useEffect(() => {
    if (ref !== undefined) rememberReferral(ref);
  }, [ref]);
  const referred = ref !== undefined || rememberedReferral() !== null;
  const carried = useCarriedValues();
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    trigger,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<SignupFormInput, unknown, SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    // "Sign up again" carries the name and address; the password never is
    // (`ux.md`, flow B3; `patterns/ui-patterns.md`, 7, "Never ask twice").
    defaultValues: { name: carried.name, email: carried.email, password: '', acceptTerms: false },
    // Nothing is checked while a field is typed into for the first time; once
    // a field shows an error it is rechecked as it is edited
    // (`patterns/ui-patterns.md`, 7).
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  // Registered in the order the fields show, so the first field with an error
  // is the one that gets focus (`patterns/ui-patterns.md`, 7).
  const name = register('name');
  const email = register('email');

  const onSubmit = handleSubmit(async (values) => {
    // Already sending, or accepted and on its way to the next page.
    if (accepted || isSubmitting) return;
    setRefusal(null);
    const outcome = await signUp(values);

    if (outcome.kind === 'success') {
      setAccepted(true);
      savePendingSignup({ name: values.name, email: values.email });
      await navigate({ to: '/signup/check-email', replace: true });
      return;
    }

    if (outcome.kind === 'field') {
      setError(outcome.field, { message: outcome.message });
      setFocus(outcome.field);
      return;
    }

    // Every field keeps its value, the password included: nothing about a
    // refused or failed sign-up is the person's fault (`ux.md`, States).
    // Focus stays on Create account, which is never disabled while it works,
    // only marked so (`ux.md`, States: Sign up).
    setRefusal(outcome);
  });

  const alert = refusal === null ? null : alertFor(refusal);
  const isBusy = isSubmitting || accepted;

  return (
    <PreAccountFrame>
      <PreAccountCard
        as="form"
        labelledBy="signup-heading"
        onSubmit={(event) => void onSubmit(event)}
        head={<PageHeading id="signup-heading">Sign up</PageHeading>}
      >
        {alert === null ? null : <DangerAlert title={alert.title}>{alert.body}</DangerAlert>}
        {referred ? (
          <InfoAlert title="You were referred">
            {`Your first ${REFERRAL_DISCOUNT_CYCLES} invoices get ${REFERRAL_DISCOUNT_PERCENT}% off once you log in.`}
          </InfoAlert>
        ) : null}

        <TextField
          {...name}
          id="name"
          label="Your name"
          autoComplete="name"
          hint="Shown in the dashboard, including to other Account Users in your account."
          error={errors.name?.message}
        />

        <TextField
          {...email}
          id="email"
          label="Email address"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          hint="You'll log in with this address. Groundly sends a verification email to it."
          error={errors.email?.message}
          onChange={(event) => {
            void email.onChange(event);
            if (errors.email !== undefined) void trigger('email');
          }}
          onBlur={(event) => {
            void email.onBlur(event);
            // The format check also runs when the field is left after a change
            // (`patterns/ui-patterns.md`, 7).
            if (dirtyFields.email === true) void trigger('email');
          }}
        />

        <TextField
          {...register('password')}
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          hint={PASSWORD_REQUIREMENTS_HINT}
          error={errors.password?.message}
          labelAction={
            <InlineAction
              aria-controls="password"
              onClick={() => setShowPassword((shown) => !shown)}
              icon={
                showPassword ? (
                  <EyeOff aria-hidden="true" strokeWidth={1.75} width={14} height={14} />
                ) : (
                  <Eye aria-hidden="true" strokeWidth={1.75} width={14} height={14} />
                )
              }
            >
              {showPassword ? 'Hide password' : 'Show password'}
            </InlineAction>
          }
        />

        <div>
          <div className={termsStyles.row}>
            <input
              {...register('acceptTerms')}
              id="accept-terms"
              type="checkbox"
              className={termsStyles.checkbox}
              aria-invalid={errors.acceptTerms === undefined ? undefined : true}
              aria-describedby={errors.acceptTerms === undefined ? undefined : 'accept-terms-error'}
            />
            <label htmlFor="accept-terms" className={termsStyles.label}>
              I agree to Groundly&apos;s{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </label>
          </div>
          {errors.acceptTerms === undefined ? null : (
            <p id="accept-terms-error" className={termsStyles.error}>
              {errors.acceptTerms.message}
            </p>
          )}
        </div>

        <Button type="submit" variant="primary" block aria-disabled={isBusy || undefined}>
          {isBusy ? 'Creating account' : 'Create account'}
        </Button>
      </PreAccountCard>

      <PreAccountFootnote>
        Already use Groundly? <Link to="/login">Log in</Link>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}

/** The danger alert each refused or failed sign-up shows (`ux.md`, Content). */
function alertFor(refusal: Refusal): { title: string; body: string } {
  switch (refusal.kind) {
    case 'too-many-attempts':
      return { title: 'Too many sign-up attempts', body: waitBody(refusal.minutes) };
    case 'no-response':
      return {
        title: "Couldn't create your account",
        body: "Groundly couldn't be reached. Check your internet connection and try again.",
      };
    case 'server-error':
      return {
        title: "Couldn't create your account",
        body: 'Groundly had a problem, so no account was created. Try again in a moment.',
      };
  }
}
